import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  IDispatchDeskExpanded,
  IDispatchDeskExpandedFlight,
  IDispatchDeskExpandedUser,
  addUserToDesk,
  removeUserFromDesk,
  addFlightsToDesk,
  removeFlightsFromDesk,
} from "src/redux/reducers/DispatchDeskReducer";
import { FlightChip, IFlightChip } from "src/components/Flights/FlightChip";
import { AddUserDialog } from "./AddUserDialog";
import { useDrop } from "react-dnd";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { resolveFullFlights } from "src/utils/dispatchDeskOpsLinkSync";

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const StyledCard = styled(Card)<{ deskcolor?: string }>(({ theme, deskcolor }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderTop: `4px solid ${deskcolor || theme.palette.primary.main}`,
  backgroundColor: theme.palette.background.paper,
}));

const UserChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.25),
  "& .MuiChip-deleteIcon": {
    fontSize: 16,
  },
}));

const FlightsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 0),
  minHeight: 40,
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  letterSpacing: "0.08em",
  marginBottom: theme.spacing(0.25),
}));

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IDispatchDeskCardProps {
  desk: IDispatchDeskExpanded;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DispatchDeskCard({ desk }: IDispatchDeskCardProps): JSX.Element {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state: IRootState) => state.authentication.attributes?._id);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  // Check if current user is already on this desk
  const isCurrentUserOnDesk = desk.users.some((u) => u._id === currentUserId);

  // -----------------------------------------------------------------------
  // Drag & drop – accept flight chips and gantt multi-select onto this desk
  // -----------------------------------------------------------------------
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ["FLIGHT", "UNASSIGNED_FLIGHT", "SELECTED_FLIGHTS"],
      drop: (item: any) => {
        let flightIds: string[] = [];
        switch (item.type) {
          case "FLIGHT":
          case "UNASSIGNED_FLIGHT":
            if (item?.flight?._id) {
              flightIds = [item.flight._id];
            }
            break;
          case "SELECTED_FLIGHTS":
            if (item?.selectedFlights?.length) {
              flightIds = item.selectedFlights.map((f: any) => f._id).filter(Boolean);
            }
            break;
          default:
            break;
        }
        if (flightIds.length > 0) {
          dispatch(addFlightsToDesk({ deskId: desk._id, flightIds }));
        }
      },
      collect: (monitor: any) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [desk._id]
  );

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleAddMe = useCallback(() => {
    if (currentUserId) {
      dispatch(addUserToDesk({ deskId: desk._id, userId: currentUserId }));
    }
  }, [dispatch, desk._id, currentUserId]);

  const handleRemoveUser = useCallback(
    (userId: string) => {
      dispatch(removeUserFromDesk({ deskId: desk._id, userId }));
    },
    [dispatch, desk._id]
  );

  const handleRemoveFlight = useCallback(
    (flightId: string) => {
      dispatch(removeFlightsFromDesk({ deskId: desk._id, flightIds: [flightId] }));
    },
    [dispatch, desk._id]
  );

  const handleClearFlights = useCallback(() => {
    const flightIds = desk.flights.map((f) => f._id);
    if (flightIds.length > 0) {
      dispatch(removeFlightsFromDesk({ deskId: desk._id, flightIds }));
    }
    setClearConfirmOpen(false);
  }, [dispatch, desk]);

  const handleClearUsers = useCallback(async () => {
    for (const u of desk.users) {
      await dispatch(removeUserFromDesk({ deskId: desk._id, userId: u._id }));
    }
    setClearConfirmOpen(false);
  }, [dispatch, desk]);

  const handleClearAll = useCallback(async () => {
    const flightIds = desk.flights.map((f) => f._id);
    if (flightIds.length > 0) {
      await dispatch(removeFlightsFromDesk({ deskId: desk._id, flightIds }));
    }
    for (const u of desk.users) {
      await dispatch(removeUserFromDesk({ deskId: desk._id, userId: u._id }));
    }
    setClearConfirmOpen(false);
  }, [dispatch, desk]);

  const handleOpenOpsLink = useCallback(async () => {
    if (desk.flights.length === 0) return;
    const flights = await resolveFullFlights(desk.flights.map((f) => f._id));
    if (flights.length === 0) return;
    dispatch(openWindow(`OpsLink — ${desk.name}`, "OpsLinkWindow", [], { initialFlights: flights }, "content"));
  }, [dispatch, desk]);

  // -----------------------------------------------------------------------
  // Map expanded flights to FlightChip shape, grouped by aircraft and
  // sorted by departure time within each group
  // -----------------------------------------------------------------------
  const flightGroups: { registration: string; items: { chip: IFlightChip; flight: IDispatchDeskExpandedFlight }[] }[] =
    useMemo(() => {
      const sorted = [...desk.flights].sort((a, b) => {
        const tA = a.scheduledOff ? new Date(a.scheduledOff).getTime() : Infinity;
        const tB = b.scheduledOff ? new Date(b.scheduledOff).getTime() : Infinity;
        return tA - tB;
      });

      const groups = new Map<string, { chip: IFlightChip; flight: IDispatchDeskExpandedFlight }[]>();
      for (const f of sorted) {
        const reg = f.aircraft?.registration ?? "Unassigned";
        if (!groups.has(reg)) groups.set(reg, []);
        groups.get(reg).push({
          chip: {
            _id: f._id,
            flightNumber: f.flightNumber,
            origin: f.origin,
            destination: f.destination,
            flightType: f.flightType,
            aircraft: f.aircraft as any,
          },
          flight: f,
        });
      }

      // Sort groups alphabetically by registration, with "Unassigned" last
      return Array.from(groups.entries())
        .sort(([a], [b]) => {
          if (a === "Unassigned") return 1;
          if (b === "Unassigned") return -1;
          return a.localeCompare(b);
        })
        .map(([registration, items]) => ({ registration, items }));
    }, [desk.flights]);

  // Build a tooltip element showing key flight times
  const buildFlightTooltip = useCallback((f: IDispatchDeskExpandedFlight): React.ReactNode => {
    const fmt = "DD-MMM-YYYY HH:mm [Z]";
    const lines: string[] = [];
    lines.push(`${f.origin} → ${f.destination}`);
    if (f.aircraft?.registration) lines.push(`Aircraft: ${f.aircraft.registration}`);
    if (f.aircraft?.aircraftType?.title) lines.push(`Type: ${f.aircraft.aircraftType.title}`);
    if (f.scheduledOff) lines.push(`STD: ${moment.utc(f.scheduledOff).format(fmt)}`);
    if (f.scheduledOn) lines.push(`STA: ${moment.utc(f.scheduledOn).format(fmt)}`);
    if (f.actualOff) lines.push(`ATD: ${moment.utc(f.actualOff).format(fmt)}`);
    if (f.actualOn) lines.push(`ATA: ${moment.utc(f.actualOn).format(fmt)}`);
    return <Box sx={{ whiteSpace: "pre-line", fontSize: 12, lineHeight: 1.5 }}>{lines.join("\n")}</Box>;
  }, []);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const dropHighlight = isOver && canDrop ? "rgba(255,255,255,0.08)" : undefined;

  return (
    <>
      <StyledCard
        deskcolor={desk.color}
        ref={drop}
        sx={{
          backgroundColor: dropHighlight,
          transition: "background-color 0.2s ease",
        }}
        variant="outlined"
      >
        <CardHeader
          title={desk.name}
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }}
          sx={{ py: 0.75, px: 1.5 }}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {desk.flights.length > 0 && (
                <Tooltip title="Open OpsLink for this desk's flights">
                  <IconButton size="small" onClick={handleOpenOpsLink} color="info">
                    <SatelliteAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!isCurrentUserOnDesk && (
                <Tooltip title="Add me to this desk">
                  <IconButton size="small" onClick={handleAddMe} color="primary">
                    <PersonAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Add user">
                <IconButton size="small" onClick={() => setAddUserOpen(true)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {(desk.flights.length > 0 || desk.users.length > 0) && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                  <Tooltip title="Clear desk">
                    <IconButton size="small" onClick={() => setClearConfirmOpen(true)} color="error">
                      <DeleteSweepIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          }
        />

        <Divider />

        <CardContent sx={{ flex: 1, py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
          {/* ---- Users ---- */}
          <SectionLabel>Dispatchers</SectionLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", mb: 0.5 }}>
            {desk.users.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                No users assigned
              </Typography>
            )}
            {desk.users.map((user: IDispatchDeskExpandedUser) => (
              <UserChip
                key={user._id}
                label={user.preferredName ?? `${user.firstName} ${user.lastName}`}
                size="small"
                variant="outlined"
                onDelete={() => handleRemoveUser(user._id)}
                deleteIcon={
                  <Tooltip title="Remove from desk">
                    <CloseIcon />
                  </Tooltip>
                }
              />
            ))}
          </Box>

          {/* ---- Flights ---- */}
          <SectionLabel>Flights</SectionLabel>
          <FlightsContainer>
            {flightGroups.length === 0 && (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 60,
                  border: "2px dashed",
                  borderColor: isOver ? "primary.main" : "divider",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isOver ? "action.hover" : "transparent",
                  transition: "all 0.2s ease",
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  Drag flights here
                </Typography>
              </Box>
            )}
            {flightGroups.map((group) => (
              <Box
                key={group.registration}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 0.75,
                  backgroundColor: "rgba(255,255,255,0.03)",
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ fontWeight: 700, fontSize: 11, display: "block", mb: 0.25 }}
                >
                  {group.registration}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {group.items.map(({ chip, flight }) => (
                    <Box key={chip._id} sx={{ display: "flex", alignItems: "center" }}>
                      <FlightChip data={chip} disableDrag disableContext tooltipContent={buildFlightTooltip(flight)} />
                      <Tooltip title="Remove flight from desk">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFlight(chip._id)}
                          sx={{ ml: -0.5, p: 0.25 }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </FlightsContainer>
        </CardContent>
      </StyledCard>

      {/* Clear desk confirmation */}
      <Dialog open={clearConfirmOpen} onClose={() => setClearConfirmOpen(false)}>
        <DialogTitle>Clear Desk — {desk.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>What would you like to remove?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
          <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleClearUsers} color="warning" variant="outlined" disabled={desk.users.length === 0}>
              Users ({desk.users.length})
            </Button>
            <Button
              onClick={handleClearFlights}
              color="warning"
              variant="outlined"
              disabled={desk.flights.length === 0}
            >
              Flights ({desk.flights.length})
            </Button>
            <Button onClick={handleClearAll} color="error" variant="contained">
              Both
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add-user dialog */}
      <AddUserDialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        deskId={desk._id}
        existingUserIds={desk.users.map((u) => u._id)}
      />
    </>
  );
}
